import { FluidColumnsRepeaterProps } from '@wix/thunderbolt-components'
import { defineRepeaterCustomElement } from './fluid-columns-repeater'
import style from './style/FluidColumnsRepeater.scss'
import React, { ComponentType, ReactNode, MouseEventHandler, useLayoutEffect, useRef } from 'react'
import _ from 'lodash'
import type { IFluidColumnsRepeater } from './types'
import {
	CellsMapType,
	IdIndexMapType,
	supportedKeyboardKeys,
	getRepeaterCells,
	getNextCell,
} from '@wix/thunderbolt-commons'

if (process.env.browser) {
	defineRepeaterCustomElement(window)
}
const useEffect = process.env.browser ? useLayoutEffect : () => {}

export type FluidColumnsRepeaterCompProps = FluidColumnsRepeaterProps & {
	id: string
	children: (itemId: string, scopeData: { parentType: string; scopeId: string; itemIndex: number }) => ReactNode
}

const FluidColumnsRepeater: ComponentType<FluidColumnsRepeaterCompProps> = ({
	id,
	children,
	items,
	horizontalGap = 0,
	verticalGap = 0,
	justifyContent = 'flex-start',
	direction = 'ltr',
	onMouseEnter = _.noop,
	onMouseLeave = _.noop,
	a11y = {},
	keyboardNavigationEnabled,
}) => {
	const { label, role } = a11y

	const wrapperId = `${id}_wrapper`
	const ref = useRef<IFluidColumnsRepeater | null>(null)

	const [rootWidth, setRootWidth] = React.useState<number>(0)
	const [cellsRefs, setCellsRefs] = React.useState<CellsMapType>([])
	const [indexMap, setIndexMap] = React.useState<IdIndexMapType>({})

	useEffect(() => {
		if (ref.current && keyboardNavigationEnabled) {
			setRootWidth(ref.current.offsetWidth)
			const { cellsMap, idIndexMap } = getRepeaterCells(items, ref.current)
			cellsMap.forEach((row, rowIndex) =>
				row.forEach((cell, columnIndex) => {
					cell.setAttribute('tabindex', rowIndex === 0 && columnIndex === 0 ? '0' : '-1')
				})
			)
			setCellsRefs(cellsMap)
			setIndexMap(idIndexMap)
		}
	}, [keyboardNavigationEnabled, items])

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (!keyboardNavigationEnabled || !ref.current) {
			return
		}

		const target = event.target as Element
		let cellsMap = cellsRefs
		let idIndexMap = indexMap

		const shouldHandleKeyboardEvent =
			supportedKeyboardKeys.includes(event.key) && target.tagName !== 'INPUT' && target.tagName !== 'SELECT'

		if (!shouldHandleKeyboardEvent || !idIndexMap[target.id]) {
			return
		}

		event.preventDefault()

		if (ref.current.offsetWidth !== rootWidth) {
			const repeaterCells = getRepeaterCells(items, ref.current)
			cellsMap = repeaterCells.cellsMap
			idIndexMap = repeaterCells.idIndexMap
			setCellsRefs(repeaterCells.cellsMap)
			setIndexMap(repeaterCells.idIndexMap)
			setRootWidth(ref.current.offsetWidth)
		}

		const cellRef: HTMLElement | undefined = getNextCell(cellsMap, idIndexMap, target.id, event.key)

		cellRef?.focus()
	}

	return (
		<div id={id} onMouseEnter={onMouseEnter as MouseEventHandler} onMouseLeave={onMouseLeave as MouseEventHandler}>
			<div className={style.wrapper} id={wrapperId}>
				<fluid-columns-repeater
					style={{ visibility: 'hidden' }}
					ref={ref}
					horizontal-gap={horizontalGap}
					vertical-gap={verticalGap}
					justify-content={justifyContent}
					direction={direction}
					container-id={wrapperId}
					items={items.length}
					class={style.repeater}
					role={role}
					aria-label={label}
					onKeyDown={handleKeyDown}
				>
					{items.map((itemId, index) =>
						children(itemId, {
							parentType: 'Repeater',
							scopeId: itemId,
							itemIndex: index,
						})
					)}
				</fluid-columns-repeater>
			</div>
		</div>
	)
}

export default FluidColumnsRepeater
